/* eslint-disable @typescript-eslint/no-explicit-any */
import { useAvailableIncidentActions } from '../../../shared/hooks/useAvailableIncidentActions'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useLightMode } from '@/hooks/useLightMode'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import { startTimer, trackEvent } from '@/utils/analytics/useAnalytics'
import { useTranslation } from 'react-i18next'
import { useStore } from 'react-redux'

import { useApolloClient } from '@apollo/client'

import { Box, Button as DarkButton, useDisclosure } from '@hivewatch/dark'

import {
  IncidentDocument,
  useCreateIncidentActionMutation,
} from '@/graphql/generated/hooks'
import { IncidentQuery } from '@/graphql/generated/operations'
import {
  IncidentActionType,
  IncidentWorkflowStatus,
} from '@/graphql/generated/schemas'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import type { AppState } from '@/redux/store'
import {
  closeIncidentMapPanelDrawer,
  closeSOPIncidentDrawer,
  closeTraceLogDrawer,
  closeWorkflowDrawer,
  openIncidentMapPanelDrawer,
  openSOPIncidentDrawer,
  openTraceLogDrawerForDevice,
  openTraceLogDrawerWithHighlight,
  openWorkflowDrawer,
  selectIncident,
  selectIncidentMapPanelDrawerIsOpen,
  selectIncidentTraceLogDrawerState,
  selectIsIncidentDrawerOpen,
  selectSOPIncidentDrawerState,
  selectWorkflowDrawerIsOpen,
  setSOPIncidentNameLike,
  toggleIncidentTraceLogDrawer,
} from '@/redux/ui/uiSlice'
import { BUTTON_PRESS } from '@/utils/analytics'
import { includeIfActive } from '@/utils/apollo'
import { formatFullName } from '@/utils/formats'

import { MixpanelDataIProps } from '../../../shared/types/types'
import { ReleaseIncidentModal } from '../../modals/ReleaseIncidentModal'
import { TakeOverIncidentModal } from '../../modals/TakeOverIncidentModal'

interface FeedButtonsIProps {
  incident: IncidentQuery['incident']
  mixpanelData: MixpanelDataIProps
}

export const FeedButtons = ({ incident, mixpanelData }: FeedButtonsIProps) => {
  const { shouldEnableIncidentActions } = usePermissions()
  const { showSuccess, showError } = useToasts({ preventDuplicate: false })
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  const isWorkflowFeatureEnabled = useFeatureFlag(
    FeatureFlag.incidentResponseWorkflow
  )
  const dispatch = useAppDispatch()
  const isWorkflowDrawerOpen = useAppSelector(selectWorkflowDrawerIsOpen)
  const sopIncidentDrawerState = useAppSelector(selectSOPIncidentDrawerState)
  const traceLogDrawerState = useAppSelector(selectIncidentTraceLogDrawerState)
  const isMapPanelDrawerOpen = useAppSelector(
    selectIncidentMapPanelDrawerIsOpen
  )
  const store = useStore<AppState>()
  const [createIncidentAction, { loading: isLoadingCreateIncidentAction }] =
    useCreateIncidentActionMutation()
  const client = useApolloClient()

  const { isAcknowledgeEnabled, isCanReleaseEnabled, isTakeOverEnabled } =
    useAvailableIncidentActions({ incident })

  const incidentId = incident?.id
  const currentOwner = formatFullName(incident?.owner ?? {}) || 'Unassigned'
  const hasWorkflowToOpenOnAcknowledge =
    Boolean(incident?.pendingWorkflow) ||
    (Boolean(incident?.workflowId) &&
      incident?.workflowStatus === IncidentWorkflowStatus.InProgress)
  const shouldOpenWorkflowOnAcknowledge =
    isWorkflowFeatureEnabled && hasWorkflowToOpenOnAcknowledge

  const {
    open: isTakeOverOpen,
    onOpen: onTakeOverOpen,
    onClose: onTakeOverClose,
  } = useDisclosure()
  const {
    open: isReleaseOpen,
    onOpen: onReleaseOpen,
    onClose: onReleaseClose,
  } = useDisclosure()

  const openWorkflowDrawerForAcknowledge = () => {
    if (!shouldOpenWorkflowOnAcknowledge || isWorkflowDrawerOpen) {
      return false
    }

    dispatch(closeSOPIncidentDrawer())
    dispatch(closeTraceLogDrawer())
    dispatch(closeIncidentMapPanelDrawer())
    dispatch(openWorkflowDrawer())

    return true
  }

  const rollbackWorkflowDrawerForAcknowledge = ({
    acknowledgementIncidentId,
    didOpenWorkflowDrawer,
    isMapPanelDrawerOpenBeforeAck,
    isSOPIncidentDrawerOpenBeforeAck,
    isTraceLogDrawerOpenBeforeAck,
    previousSOPNameLike,
    previousTraceLogState,
  }: {
    acknowledgementIncidentId: string | undefined
    didOpenWorkflowDrawer: boolean
    isMapPanelDrawerOpenBeforeAck: boolean
    isSOPIncidentDrawerOpenBeforeAck: boolean
    isTraceLogDrawerOpenBeforeAck: boolean
    previousSOPNameLike: string | null
    previousTraceLogState: {
      highlightedEvent: { detectedAt: string; externalId: string } | null
      deviceId: string | null
      shouldForceDeviceTab: boolean
    }
  }) => {
    const currentIncidentId = selectIncident(store.getState()).id
    if (
      !didOpenWorkflowDrawer ||
      currentIncidentId !== acknowledgementIncidentId
    ) {
      return false
    }

    dispatch(closeWorkflowDrawer())
    if (!selectIsIncidentDrawerOpen(store.getState())) {
      return false
    }

    if (isSOPIncidentDrawerOpenBeforeAck) {
      dispatch(openSOPIncidentDrawer())
      if (previousSOPNameLike !== null) {
        dispatch(setSOPIncidentNameLike(previousSOPNameLike))
      }
    }
    if (isTraceLogDrawerOpenBeforeAck) {
      const { highlightedEvent, deviceId, shouldForceDeviceTab } =
        previousTraceLogState
      if (shouldForceDeviceTab && deviceId) {
        // Restore a Reasoning Trail device deep-link (device filter + Device tab)
        dispatch(openTraceLogDrawerForDevice({ deviceId, highlightedEvent }))
      } else if (highlightedEvent) {
        dispatch(openTraceLogDrawerWithHighlight(highlightedEvent))
      } else {
        dispatch(toggleIncidentTraceLogDrawer())
      }
    }
    if (isMapPanelDrawerOpenBeforeAck) {
      dispatch(openIncidentMapPanelDrawer())
    }

    return true
  }

  const onAcknowledgementClick = async () => {
    const eventName = `${BUTTON_PRESS} Incident Acknowledge`
    startTimer(eventName)
    const acknowledgementIncidentId = incidentId
    const isSOPIncidentDrawerOpenBeforeAck = Boolean(
      sopIncidentDrawerState?.isOpen
    )
    const previousSOPNameLike = sopIncidentDrawerState?.sopNameLike ?? null
    const isTraceLogDrawerOpenBeforeAck = Boolean(traceLogDrawerState?.isOpen)
    const isMapPanelDrawerOpenBeforeAck = isMapPanelDrawerOpen
    const previousTraceLogState = {
      highlightedEvent: traceLogDrawerState?.highlightedEvent ?? null,
      deviceId: traceLogDrawerState?.deviceId ?? null,
      shouldForceDeviceTab: traceLogDrawerState?.shouldForceDeviceTab ?? false,
    }
    let didOpenWorkflowDrawer = false
    try {
      didOpenWorkflowDrawer = openWorkflowDrawerForAcknowledge()

      await createIncidentAction({
        variables: {
          input: {
            incidentId,
            type: IncidentActionType.AcknowledgementByOperator,
          },
        },
        // Not awaited so a failed refetch can't fail an already-committed action
        // (apollo-data-fetching.md). NextUpIncident keeps the Priority Queue
        // Next Up/Resume target fresh after this status change — included only
        // while it's mounted (PQ active) to avoid the "Unknown query" dev warning.
        refetchQueries: [
          { query: IncidentDocument, variables: { id: incidentId } },
          'GlobalUnresolvedIncidentsCount',
          ...includeIfActive(client, 'NextUpIncident'),
        ],
      })
      trackEvent(eventName, mixpanelData)
      showSuccess(t('incidents:incident_acknowledged'))
    } catch (e) {
      rollbackWorkflowDrawerForAcknowledge({
        acknowledgementIncidentId,
        didOpenWorkflowDrawer,
        isMapPanelDrawerOpenBeforeAck,
        isSOPIncidentDrawerOpenBeforeAck,
        isTraceLogDrawerOpenBeforeAck,
        previousSOPNameLike,
        previousTraceLogState,
      })
      showError(e)
    }
  }

  const onReleaseClick = async () => {
    trackEvent(`${BUTTON_PRESS} Incident Release`, mixpanelData)
    try {
      await createIncidentAction({
        variables: {
          input: {
            incidentId,
            type: IncidentActionType.ReleaseByOperator,
          },
        },
        // Not awaited so a failed refetch can't fail an already-committed action
        // (apollo-data-fetching.md). NextUpIncident keeps the Priority Queue
        // Next Up/Resume target fresh after this status change — included only
        // while it's mounted (PQ active) to avoid the "Unknown query" dev warning.
        refetchQueries: [
          { query: IncidentDocument, variables: { id: incidentId } },
          'GlobalUnresolvedIncidentsCount',
          ...includeIfActive(client, 'NextUpIncident'),
        ],
      })
      onReleaseClose()
      showSuccess(t('incidents:incident_released'))
    } catch (e) {
      showError(e)
    }
  }

  const onTakeOverClick = async () => {
    trackEvent(`${BUTTON_PRESS} Incident Take Over & Acknowledge`, mixpanelData)
    const acknowledgementIncidentId = incidentId
    const isSOPIncidentDrawerOpenBeforeAck = Boolean(
      sopIncidentDrawerState?.isOpen
    )
    const previousSOPNameLike = sopIncidentDrawerState?.sopNameLike ?? null
    const isTraceLogDrawerOpenBeforeAck = Boolean(traceLogDrawerState?.isOpen)
    const isMapPanelDrawerOpenBeforeAck = isMapPanelDrawerOpen
    const previousTraceLogState = {
      highlightedEvent: traceLogDrawerState?.highlightedEvent ?? null,
      deviceId: traceLogDrawerState?.deviceId ?? null,
      shouldForceDeviceTab: traceLogDrawerState?.shouldForceDeviceTab ?? false,
    }
    let didOpenWorkflowDrawer = false
    try {
      didOpenWorkflowDrawer = openWorkflowDrawerForAcknowledge()
      if (didOpenWorkflowDrawer) {
        onTakeOverClose()
      }

      await createIncidentAction({
        variables: {
          input: {
            incidentId,
            type: IncidentActionType.AcknowledgementByOperator,
          },
        },
        // Not awaited so a failed refetch can't fail an already-committed action
        // (apollo-data-fetching.md). NextUpIncident keeps the Priority Queue
        // Next Up/Resume target fresh after this status change — included only
        // while it's mounted (PQ active) to avoid the "Unknown query" dev warning.
        refetchQueries: [
          { query: IncidentDocument, variables: { id: incidentId } },
          // The Incidents table runs IncidentsTable instead of Incidents while
          // noTotalIncidentCount is on (ENG-34177), so name both and let
          // includeIfActive pick whichever is mounted — the skipped one sits
          // in standby, which the "active" lookup excludes.
          ...includeIfActive(client, 'Incidents'),
          ...includeIfActive(client, 'IncidentsTable'),
          ...includeIfActive(client, 'NextUpIncident'),
        ],
      })
      onTakeOverClose()
      showSuccess(t('incidents:incident_taken_over'))
    } catch (e) {
      const didRollbackWorkflowDrawer = rollbackWorkflowDrawerForAcknowledge({
        acknowledgementIncidentId,
        didOpenWorkflowDrawer,
        isMapPanelDrawerOpenBeforeAck,
        isSOPIncidentDrawerOpenBeforeAck,
        isTraceLogDrawerOpenBeforeAck,
        previousSOPNameLike,
        previousTraceLogState,
      })
      if (didOpenWorkflowDrawer && didRollbackWorkflowDrawer) {
        onTakeOverOpen()
      }
      showError(e)
    }
  }
  return (
    <>
      {isAcknowledgeEnabled && (
        <Box mt='4'>
          <DarkButton
            colorScheme={colorScheme}
            data-testid='incidentDrawer_feed_acknowledge'
            disabled={!shouldEnableIncidentActions}
            isLoading={isLoadingCreateIncidentAction}
            onClick={onAcknowledgementClick}
            size='lg'
            variant='primary'
          >
            Acknowledge
          </DarkButton>
        </Box>
      )}
      {isTakeOverEnabled && (
        <Box mt='4'>
          <DarkButton
            colorScheme={colorScheme}
            data-testid='incidentDrawer_feed_takeOverAndAcknowledge'
            isLoading={isLoadingCreateIncidentAction}
            onClick={onTakeOverOpen}
            size='lg'
            variant='primary'
          >
            Take Over & Acknowledge
          </DarkButton>
        </Box>
      )}
      {isCanReleaseEnabled && (
        <Box mt='4'>
          <DarkButton
            colorScheme={colorScheme}
            data-testid='incidentDrawer_feed_release'
            isLoading={isLoadingCreateIncidentAction}
            onClick={onReleaseOpen}
            size='lg'
            variant='secondary'
          >
            Release
          </DarkButton>
        </Box>
      )}
      {isReleaseOpen && isCanReleaseEnabled && (
        <ReleaseIncidentModal
          displayId={incident?.displayId}
          incidentName={incident?.name}
          isLoading={isLoadingCreateIncidentAction}
          isOpen={isReleaseOpen}
          onClose={onReleaseClose}
          onSubmit={onReleaseClick}
        />
      )}
      {isTakeOverOpen && isTakeOverEnabled && (
        <TakeOverIncidentModal
          currentOwner={currentOwner}
          displayId={incident?.displayId}
          incidentName={incident?.name}
          isLoading={isLoadingCreateIncidentAction}
          isOpen={isTakeOverOpen}
          nextAction={IncidentActionType.AcknowledgementByOperator}
          onClose={onTakeOverClose}
          onSubmit={onTakeOverClick}
        />
      )}
    </>
  )
}
